import React, { Fragment, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants';

interface KYCUser {
  id: string;
  email: string;
  walletAddress: string;
  username: string;
  createdAt: string;
  role: string;
}

interface KYCApplication {
  id: string;
  userId: string;
  fullName: string;
  address: string;
  phoneNumber: string;
  documentType: string;
  documentUrl: string;
  documentVerificationUrl: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  user: KYCUser;
  reviewer?: {
    id: string;
    email: string;
    username: string;
  };
}

interface PaginationInfo {
  total: number;
  page: number;
  pages: number;
  limit: number;
}

const KYCAdminPanel: React.FC = () => {
  const [applications, setApplications] = useState<KYCApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<KYCApplication | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { user, token } = useAuth();

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchKYCApplications();
    }
  }, [user, token, selectedStatus, currentPage]);

  const fetchKYCApplications = async () => {
    try {
      setLoading(true);
      const url = selectedStatus === 'all'
        ? `${API_URL}/api/kyc/all?page=${currentPage}&limit=10`
        : selectedStatus === 'pending'
          ? `${API_URL}/api/kyc/pending`
          : `${API_URL}/api/kyc/all?status=${selectedStatus}&page=${currentPage}&limit=10`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
        if (data.pagination) {
          setPagination(data.pagination);
        }
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch KYC applications');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (applicationId: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      const response = await fetch(`${API_URL}/api/kyc/status/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          rejectionReason
        })
      });

      if (response.ok) {
        setSuccess(`Application ${status} successfully`);
        setApplications(prev => prev.filter(app => app.id !== applicationId));
        setShowModal(false);
        setSelectedApplication(null);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${status} application`);
      }
    } catch (error) {
      console.error('Status update error:', error);
      setError('Network error. Please try again.');
    }
  };

  const openApplicationModal = (application: KYCApplication) => {
    setSelectedApplication(application);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'badge bg-warning text-dark',
      approved: 'badge bg-success',
      rejected: 'badge bg-danger'
    };
    return badges[status as keyof typeof badges] || 'badge bg-secondary';
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Fragment>
        <div className="container-fluid py-2">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center p-0">
                  <i className="feather-alert-triangle font-xl text-warning mb-3"></i>
                  <h4 className="fw-700 text-grey-900">Access Denied</h4>
                  <p className="text-grey-500 font-xsss">You don't have permission to access this page.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="container-fluid py-2">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-xss w-100 d-block d-flex border-0 p-4 mb-3">
              <div className="card-body d-flex align-items-center justify-content-between p-0">
                <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900">KYC Management Panel</h2>

                {/* Status Filter */}
                <div className="d-flex gap-2">
                  <select
                    className="form-select font-xsss"
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="all">All Applications</option>
                  </select>

                  <button
                    className="btn btn-primary font-xsss px-3"
                    onClick={fetchKYCApplications}
                    disabled={loading}
                  >
                    <i className="feather-refresh-cw me-1"></i>
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Status Summary Cards */}
            <div className="row mb-3">
              <div className="col-md-3">
                <div className="card border-0 shadow-xss p-3">
                  <div className="text-center">
                    <i className="feather-clock font-lg text-warning mb-2"></i>
                    <h5 className="fw-600 text-grey-900 font-xsss mb-1">Pending</h5>
                    <p className="text-grey-500 font-xssss mb-0">
                      {applications.filter(app => app.status === 'pending').length} applications
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-xss p-3">
                  <div className="text-center">
                    <i className="feather-check-circle font-lg text-success mb-2"></i>
                    <h5 className="fw-600 text-grey-900 font-xsss mb-1">Approved</h5>
                    <p className="text-grey-500 font-xssss mb-0">
                      {applications.filter(app => app.status === 'approved').length} applications
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-xss p-3">
                  <div className="text-center">
                    <i className="feather-x-circle font-lg text-danger mb-2"></i>
                    <h5 className="fw-600 text-grey-900 font-xsss mb-1">Rejected</h5>
                    <p className="text-grey-500 font-xssss mb-0">
                      {applications.filter(app => app.status === 'rejected').length} applications
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-xss p-3">
                  <div className="text-center">
                    <i className="feather-file-text font-lg text-primary mb-2"></i>
                    <h5 className="fw-600 text-grey-900 font-xsss mb-1">Total</h5>
                    <p className="text-grey-500 font-xssss mb-0">
                      {pagination?.total || applications.length} applications
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="alert alert-danger font-xsss fw-500 mb-3" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="alert alert-success font-xsss fw-500 mb-3" role="alert">
                {success}
              </div>
            )}

            {loading ? (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center p-0">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-grey-500">Loading KYC applications...</p>
                </div>
              </div>
            ) : applications.length === 0 ? (
              <div className="card shadow-xss w-100 d-block border-0 p-4 mb-3">
                <div className="card-body text-center p-0">
                  <i className="feather-inbox font-xl text-grey-400 mb-3"></i>
                  <h4 className="fw-700 text-grey-900">No Applications Found</h4>
                  <p className="text-grey-500 font-xsss">No KYC applications match the current filter.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Applications Table */}
                <div className="card shadow-xss w-100 d-block border-0 mb-3">
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th className="font-xsss fw-600 text-grey-700 px-4 py-3">User</th>
                            <th className="font-xsss fw-600 text-grey-700 px-4 py-3">Full Name</th>
                            <th className="font-xsss fw-600 text-grey-700 px-4 py-3">Document Type</th>
                            <th className="font-xsss fw-600 text-grey-700 px-4 py-3">Status</th>
                            <th className="font-xsss fw-600 text-grey-700 px-4 py-3">Submitted</th>
                            <th className="font-xsss fw-600 text-grey-700 px-4 py-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {applications.map((application) => (
                            <tr key={application.id}>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-xsss fw-600 text-grey-900 mb-0">
                                    {application.user.username || application.user.email}
                                  </p>
                                  <p className="font-xssss text-grey-500 mb-0">
                                    {application.user.walletAddress?.substring(0, 8)}...
                                  </p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-xsss text-grey-900 mb-0">{application.fullName}</p>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-xsss text-grey-700">
                                  {application.documentType.replace('_', ' ').toUpperCase()}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`${getStatusBadge(application.status)} font-xssss px-2 py-1`}>
                                  {application.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-xsss text-grey-500 mb-0">
                                  {formatDate(application.submittedAt)}
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  className="btn btn-sm btn-outline-primary font-xssss px-2 py-1"
                                  onClick={() => openApplicationModal(application)}
                                >
                                  <i className="feather-eye me-1"></i>
                                  Review
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                  <div className="card shadow-xss w-100 d-block border-0 p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="font-xsss text-grey-500 mb-0">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                      </p>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={pagination.page <= 1}
                        >
                          Previous
                        </button>
                        {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              className={`btn btn-sm ${page === pagination.page ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          );
                        })}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
                          disabled={pagination.page >= pagination.pages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Application Review Modal */}
      {showModal && selectedApplication && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-600">KYC Application Review</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6 className="fw-600 text-grey-700 mb-2">User Information</h6>
                    <p className="font-xsss mb-1"><strong>Email:</strong> {selectedApplication.user.email}</p>
                    <p className="font-xsss mb-1"><strong>Username:</strong> {selectedApplication.user.username || 'Not set'}</p>
                    <p className="font-xsss mb-1"><strong>Role:</strong> {selectedApplication.user.role}</p>
                    <p className="font-xsss mb-1"><strong>Wallet:</strong> {selectedApplication.user.walletAddress}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-600 text-grey-700 mb-2">Application Status</h6>
                    <p className="font-xsss mb-1">
                      <strong>Status:</strong>
                      <span className={`ms-2 ${getStatusBadge(selectedApplication.status)} font-xssss px-2 py-1`}>
                        {selectedApplication.status.toUpperCase()}
                      </span>
                    </p>
                    <p className="font-xsss mb-1"><strong>Submitted:</strong> {formatDate(selectedApplication.submittedAt)}</p>
                    {selectedApplication.reviewedAt && (
                      <p className="font-xsss mb-1"><strong>Reviewed:</strong> {formatDate(selectedApplication.reviewedAt)}</p>
                    )}
                    {selectedApplication.reviewer && (
                      <p className="font-xsss mb-1"><strong>Reviewer:</strong> {selectedApplication.reviewer.email}</p>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <h6 className="fw-600 text-grey-700 mb-2">Personal Information</h6>
                    <p className="font-xsss mb-1"><strong>Full Name:</strong> {selectedApplication.fullName}</p>
                    <p className="font-xsss mb-1"><strong>Phone:</strong> {selectedApplication.phoneNumber}</p>
                    <p className="font-xsss mb-1"><strong>Document Type:</strong> {selectedApplication.documentType.replace('_', ' ').toUpperCase()}</p>
                    <p className="font-xsss mb-1"><strong>Address:</strong></p>
                    <p className="font-xsss text-grey-600 mb-3">{selectedApplication.address}</p>
                  </div>
                </div>

                {selectedApplication.rejectionReason && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <h6 className="fw-600 text-danger mb-2">Rejection Reason</h6>
                      <p className="font-xsss text-grey-700 bg-light p-3 rounded">{selectedApplication.rejectionReason}</p>
                    </div>
                  </div>
                )}

                <div className="row mb-4">
                  <div className="col-md-6">
                    <h6 className="fw-600 text-grey-700 mb-2">Document Photo</h6>
                    <div className="border rounded p-2">
                      <img
                        src={selectedApplication.documentUrl}
                        alt="Document"
                        className="w-100 rounded"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2Yzc1N2QiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <a
                        href={selectedApplication.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary mt-2 w-100"
                      >
                        <i className="feather-external-link me-1"></i>
                        View Full Size
                      </a>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-600 text-grey-700 mb-2">Verification Photo</h6>
                    <div className="border rounded p-2">
                      <img
                        src={selectedApplication.documentVerificationUrl}
                        alt="Verification"
                        className="w-100 rounded"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2Yzc1N2QiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <a
                        href={selectedApplication.documentVerificationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary mt-2 w-100"
                      >
                        <i className="feather-external-link me-1"></i>
                        View Full Size
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {selectedApplication.status === 'pending' && (
                  <>
                    <button
                      className="btn btn-success px-4 font-xsss fw-600"
                      onClick={() => handleStatusUpdate(selectedApplication.id, 'approved')}
                    >
                      <i className="feather-check me-1"></i>
                      Approve
                    </button>
                    <button
                      className="btn btn-danger px-4 font-xsss fw-600"
                      onClick={() => {
                        const reason = prompt('Please provide a rejection reason:');
                        if (reason) {
                          handleStatusUpdate(selectedApplication.id, 'rejected', reason);
                        }
                      }}
                    >
                      <i className="feather-x me-1"></i>
                      Reject
                    </button>
                  </>
                )}
                <button
                  className="btn btn-secondary px-4 font-xsss fw-600"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default KYCAdminPanel;